<div class="ledger" [class.is-loading]="loading">

  <!-- slim progress line while first load is in flight -->
  <div class="load-rail" *ngIf="loading"><span></span></div>

  <!-- HEADER -->
  <header class="ledger-head">

    <div class="ledger-head-copy">

      <p class="eyebrow">Business Overview</p>

      <h1 class="ledger-title">
        <span class="mark">§</span>
        Admin Dashboard
      </h1>

      <p class="ledger-sub">
        Monitor sales, inventory, orders &amp; business performance
      </p>

    </div>

    <div class="ledger-head-tools">

      <span class="live-stamp">
        <i class="dot"></i>
        Live
      </span>

      <div class="tab-actions">

        <button class="tab-btn" routerLink="/admin/products">
          <i class="fa-solid fa-plus"></i>
          Add Product
        </button>

        <button class="tab-btn" routerLink="/admin/orders">
          <i class="fa-solid fa-box"></i>
          Orders
        </button>

        <button class="tab-btn" routerLink="/admin/profile">
          <i class="fa-solid fa-user"></i>
          Profile
        </button>

        <button class="tab-btn tab-btn--logout" (click)="logout()">
          <i class="fa-solid fa-right-from-bracket"></i>
          Logout
        </button>

      </div>

    </div>

  </header>

  <!-- LEDGER ENTRIES (STATS) -->
  <section class="entry-grid">

    <article class="entry entry--forest" routerLink="/admin/analytics">
      <div class="entry-row">
        <span class="entry-icon">💰</span>
        <span class="entry-label">Total Revenue</span>
      </div>
      <h2 class="entry-value">{{ totalRevenue | number | bnNumber:'৳ ' }}</h2>
      <p class="entry-desc">Total completed sales revenue</p>
    </article>

    <article class="entry entry--brass" routerLink="/admin/finance">
      <div class="entry-row">
        <span class="entry-icon">📈</span>
        <span class="entry-label">Total Profit</span>
      </div>
      <h2 class="entry-value">{{ totalProfit | number | bnNumber:'৳ ' }}</h2>
      <p class="entry-desc">Net business earnings</p>
    </article>

    <article class="entry entry--slate" routerLink="/admin/inventory">
      <div class="entry-row">
        <span class="entry-icon">🏦</span>
        <span class="entry-label">Total Invest</span>
      </div>
      <h2 class="entry-value">{{ totalInvest | number | bnNumber:'৳ ' }}</h2>
      <p class="entry-desc">Current inventory investment</p>
    </article>

    <article class="entry entry--ink" routerLink="/admin/orders">
      <div class="entry-row">
        <span class="entry-icon">📦</span>
        <span class="entry-label">Total Orders</span>
      </div>
      <h2 class="entry-value">{{ totalOrders }}</h2>
      <p class="entry-desc">Customer orders received</p>
    </article>

    <article class="entry entry--plum" routerLink="/admin/products">
      <div class="entry-row">
        <span class="entry-icon">🛒</span>
        <span class="entry-label">Total Products</span>
      </div>
      <h2 class="entry-value">{{ totalProducts }}</h2>
      <p class="entry-desc">Products available in store</p>
    </article>

    <article class="entry entry--red" routerLink="/admin/complaints">
      <div class="entry-row">
        <span class="entry-icon">⚠️</span>
        <span class="entry-label">Complaints</span>
      </div>
      <h2 class="entry-value">{{ totalComplaints }}</h2>
      <p class="entry-desc">Customer complaints &amp; reports</p>
    </article>

  </section>

  <!-- QUICK ACTIONS -->
  <section class="quick-row">

    <button class="pill-btn" routerLink="/admin/products">
      <i class="fa-solid fa-plus"></i>
      Add Product
    </button>

    <button class="pill-btn" routerLink="/admin/purchase-management">
      <i class="fa-solid fa-cart-plus"></i>
      Purchase Stock
    </button>

    <button class="pill-btn" routerLink="/admin/orders">
      <i class="fa-solid fa-box"></i>
      Manage Orders
    </button>

    <button class="pill-btn" routerLink="/admin/analytics">
      <i class="fa-solid fa-chart-simple"></i>
      Analytics
    </button>

  </section>

  <!-- BEST SALES -->
  <section class="ledger-section">

    <div class="section-head">

      <div>
        <p class="eyebrow eyebrow--pink">Top Selling</p>
        <h2 class="section-title">Best Sales</h2>
      </div>

      <span class="section-note">Most sold products</span>

    </div>

    <div class="product-grid" *ngIf="bestSales.length; else emptyBest">

      <article class="product-card" *ngFor="let p of bestSales">

        <div class="product-photo">
          <img [src]="getImage(p.imageUrl)" [alt]="p.name" (error)="onImgError($event)" />
        </div>

        <div class="product-body">

          <h3>{{ languageService.productName(p) }}</h3>

          <div class="tag-row">
            <span class="tag tag--sold">Sold {{ p.sold }}</span>
            <span class="tag tag--stock">{{ p.stock | bnNumber }} left</span>
          </div>

        </div>

      </article>

    </div>

    <ng-template #emptyBest>
      <p class="empty-note">No sales data yet — figures will appear once orders come in.</p>
    </ng-template>

  </section>

  <!-- INVENTORY -->
  <section class="inventory-grid">

    <!-- LOW STOCK -->
    <div class="inv-card inv-card--amber">

      <div class="section-head">
        <div>
          <p class="eyebrow eyebrow--amber">Warning</p>
          <h2 class="section-title section-title--sm">Low Stock</h2>
        </div>
      </div>

      <div class="inv-row" *ngFor="let p of lowStock">
        <img [src]="getImage(p.imageUrl)" [alt]="p.name" (error)="onImgError($event)" />
        <div>
          <h4>{{ languageService.productName(p) }}</h4>
          <span>Only {{ p.stock | bnNumber }} left</span>
        </div>
      </div>

      <p class="empty-note" *ngIf="!lowStock.length">Stock levels look healthy right now.</p>

    </div>

    <!-- OUT OF STOCK -->
    <div class="inv-card inv-card--red">

      <div class="section-head">
        <div>
          <p class="eyebrow eyebrow--red">Critical</p>
          <h2 class="section-title section-title--sm">Out Of Stock</h2>
        </div>
      </div>

      <div class="inv-row" *ngFor="let p of outOfStock">
        <img [src]="getImage(p.imageUrl)" [alt]="p.name" (error)="onImgError($event)" />
        <div>
          <h4>{{ languageService.productName(p) }}</h4>
          <span>Refill immediately</span>
        </div>
      </div>

      <p class="empty-note" *ngIf="!outOfStock.length">Nothing out of stock. Nice work.</p>

    </div>

  </section>

</div>
